import type { Component, FileMetadata } from '../design/components';
import type { Context, ReactiveEntity } from '../design/app';
import type { Action, Entity } from '../design/types';

/**
 * Safely stringify a value, handling circular references and complex objects.
 */
function safeStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') return `"${value}"`;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    // For arrays of entities, just show IDs or brief info
    if (value.length === 0) return '[]';
    if (value[0] && typeof value[0] === 'object' && 'id' in value[0]) {
      return `[${value.map((v: Entity) => v.name || v.id).join(', ')}]`;
    }
    return `[${value.length} items]`;
  }
  if (typeof value === 'object') {
    // For single entity references, show name or id
    const obj = value as Record<string, unknown>;
    if ('id' in obj) {
      return String(obj.name || obj.id);
    }
    return '[object]';
  }
  return String(value);
}

/**
 * Builds a dynamic system prompt for the AI assistant based on current context.
 * Provides awareness of the system, user, and current page state.
 */
export function buildSystemPrompt(context: Context, files?: FileMetadata[]): string {
  const { user, authn, page, project } = context;

  // Extract models from authorizations
  const models = authn
    .map(a => a.capability?.model)
    .filter((m): m is NonNullable<typeof m> => m != null && !m.hidden);

  // Build model list (brief - use describe_model tool for details)
  const modelList = models
    .map(m => `- ${m.name} (${m.path})`)
    .join('\n');

  // Current page info
  const currentModel = page.current?.model;
  const entities = page.current?.reactiveEntities ?? [];

  let currentPageSection = '';
  if (currentModel) {
    const isListView = currentModel.type === 'collection' && entities.length !== 1;
    currentPageSection = `
## Current Page

Viewing: **${currentModel.name}** at \`${currentModel.path}\`
Type: ${isListView ? 'List view' : 'Detail/Edit view'}
${!isListView && entities.length === 1 ? `Entity ID: ${entities[0].entity?.id || 'new (unsaved)'}` : ''}

### Current Data
${isListView
  ? `${entities.length} items. Use \`list_entities\` to query.`
  : entities.map((re: ReactiveEntity) => {
    return re.components
      .filter((c: Component) => c.name && c.state?.[0] !== undefined && c.state?.[0] !== '')
      .map((c: Component) => `  - ${c.name}: ${safeStringify(c.state?.[0])}`)
      .join('\n');
  }).join('\n\n')}

### Available Actions
${currentModel.actions.map((a: Action) => `- \`${a.name}\``).join('\n')}
`;
  }

  const language = navigator?.language || 'en';

  return `You are Ada, the user's personal agent for ${project?.name || 'this application'}. You help users navigate the system and perform actions on their behalf. Always respond in the user's language: ${language}. This is a model-driven application that renders UI dynamically from Model definitions.

## Available Models

${modelList}

Use the \`describe_model\` tool to get full schema details for any model.

## Current User

${user ? `Name: ${user.name}\nEmail: ${user.email}` : 'Not logged in'}
${currentPageSection}
## Your Tools

1. **list_entities(model, filter?)** - Query entities from a model
2. **get_entity(model, id)** - Get a specific entity by ID
3. **describe_model(model)** - Get full schema of a model
4. **update(name, value)** - Update a field on the current page. For file properties, use **update(name, file: index)** with the file index from attached files. **For reference and object-with-reference fields: ALWAYS pass the full entity object** (\`{id, name, ...}\`) that you already have from a prior \`list_entities\` or \`get_entity\` result — the entire result row, not just the id. Passing an id string is a last resort and forces the tool to re-fetch the entity from the database (extra cost, extra latency). Multi-valued references take an array of objects. Pass \`null\` or \`[]\` to clear
5. **run(action)** - Execute an action (e.g., "Save", "Delete")
6. **navigate(path)** - Go to a URL. The first segment is the model's **\`path\`** field (the URL-facing one, shown in parentheses in the model list above), **never** the model's \`name\`. E.g. for a model listed as \`Documentos (/documents)\`, navigate to \`/documents\`, NOT \`/Documentos\`
7. **console_log(message)** - Log for debugging

## Navigation Patterns

- \`/{path}\` - List view (e.g., /users)
- \`/{path}/{id}\` - Edit existing entity
- \`/{path}/new\` - Create new entity

**CRITICAL: \`{path}\` is the model's \`path\` field, NOT its \`name\`.** The name and the path are often similar but not guaranteed to match, and paths are always lowercase / URL-safe while names are human-readable. Look up each model's \`path\` from the model list above before calling \`navigate\`.

- ❌ Wrong: \`navigate("/Documentos/abc-123")\` — uses the name \`Documentos\` from the model list
- ✅ Right: \`navigate("/documents/abc-123")\` — uses the \`path\` \`/documents\` shown in parentheses

Navigating to a name instead of a path lands on a 404-equivalent empty page and subsequent \`update\` / \`run\` calls operate on nothing.

${files && files.length > 0 ? `## Attached Files

The user has attached files to this message. Use \`update(name, file: index)\` to set them on image/pdf properties.
${files.map((f, i) => `- [${i}] ${f.name} (${f.type}, ${Math.round(f.size / 1024)} KB)`).join('\n')}

` : ''}## Guidelines

- Greet the user warmly on first message
- Keep responses short — one or two sentences when possible
- Lead with the answer or action, skip preamble
- Use markdown for readability (bold, lists)
- No emojis
- When asked to save, use the \`run\` tool with "Save" or "Update" action
- For reference / object-with-reference fields, **reuse existing entities** — call \`list_entities\` and pick the best match by name / label. If no match exists, **stop and ask the user** — do NOT silently create the referenced entity on their behalf. Only create when the user explicitly asks for a new one

## Multi-Step Actions (IMPORTANT)

When using 2+ tools, wrap intermediate steps in a steps block:

\`\`\`steps
Navigated to /users/new
Set name to "Samantha"
Saved user
\`\`\`

Created user **Samantha**.

Do NOT narrate verbosely ("Let me... Great! Now let me... Perfect!"). Just list steps briefly, then the result.
`;
}
